/**
 * SiiStatusTab — lo que mide la vigilancia del SII y el control manual del aviso.
 *
 * La vigilancia (`apps/sales/vigilancia_sii.py`, cada 5 min) enciende el aviso
 * «Demora en el SII» cuando envíos de 2 o más empresas esperan más de 20 min,
 * y lo muestra solo a los tenants con envíos esperando. Acá el platform admin
 * puede forzarlo (una mantención anunciada por el SII, que la vigilancia no ve
 * hasta que hay atraso) o apagarlo (un falso positivo), y cambiar el texto.
 */
import { useEffect, useState } from 'react'
import { Hourglass, Loader2, Save } from 'lucide-react'
import { useSiiServiceStatus, useUpdateSiiServiceStatus } from '../hooks/usePlatform'
import type { SiiNoticeMode } from '../hooks/usePlatform'
import { Button } from '@/components/Button'
import { ToastContainer, useToast } from '@/components/Toast'
import { extractErrorMessage } from '@/lib/errorUtils'
import { TextField } from '@/components/ds/TextField'
import { SegmentGroup } from '@/components/ds/SegmentGroup'

const MODOS: { value: SiiNoticeMode; label: string; hint: string }[] = [
  { value: 'auto', label: 'Automático', hint: 'Lo enciende la vigilancia y solo lo ven los tenants con envíos esperando.' },
  { value: 'forced_on', label: 'Encendido', hint: 'Lo ven todos los tenants de Chile, haya o no atraso.' },
  { value: 'forced_off', label: 'Apagado', hint: 'Nadie lo ve, aunque la vigilancia detecte atraso.' },
]

function hora(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-CL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function Dato({ etiqueta, valor, alerta = false }: { etiqueta: string; valor: string | number; alerta?: boolean }) {
  return (
    <div className="p-3 rounded-lg border border-divider bg-surface">
      <p className="text-xs text-content-muted">{etiqueta}</p>
      <p className={`text-lg font-semibold mt-0.5 ${alerta ? 'text-danger-content-strong' : 'text-content'}`}>{valor}</p>
    </div>
  )
}

export function SiiStatusTab() {
  const toast = useToast()
  const { data: estado, isLoading } = useSiiServiceStatus()
  const actualizar = useUpdateSiiServiceStatus()
  const [modo, setModo] = useState<SiiNoticeMode>('auto')
  const [mensaje, setMensaje] = useState('')

  useEffect(() => {
    if (!estado) return
    setModo(estado.mode)
    setMensaje(estado.message)
  }, [estado?.mode, estado?.message]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || !estado) {
    return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-content-muted" /></div>
  }

  const cambios = modo !== estado.mode || mensaje !== estado.message
  const textoFinal = mensaje.trim() || estado.default_message
  const visible = modo === 'forced_on' || (modo === 'auto' && estado.is_slow)

  const guardar = () => {
    actualizar.mutate(
      { mode: modo, message: mensaje },
      {
        onSuccess: () => toast.success('Aviso actualizado.'),
        onError: (e) => toast.error(extractErrorMessage(e, 'No se pudo actualizar el aviso.')),
      },
    )
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-content">Estado del SII</h3>
        <p className="text-xs text-content-muted mt-0.5 max-w-2xl">
          La vigilancia revisa cada 5 minutos los envíos al SII de todos los tenants de Chile. Si envíos de
          2 o más empresas esperan respuesta más de 20 minutos en dos revisiones seguidas, se enciende el
          aviso; se apaga solo tras dos revisiones normales. Si las consultas se detienen o el SII no
          responde, llega un correo a los administradores de plataforma.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Dato etiqueta="Detectado" valor={estado.is_slow ? `Lento desde ${hora(estado.slow_since)}` : 'Normal'} alerta={estado.is_slow} />
        <Dato etiqueta="Envíos atrasados (> 20 min)" valor={`${estado.waiting_documents} de ${estado.affected_taxpayers} empresa(s)`} />
        <Dato etiqueta="Sin consultar (problema de Clobi)" valor={estado.stalled_documents} alerta={estado.stalled_documents > 0} />
        <Dato etiqueta="SII sin responder" valor={estado.sii_unreachable ? 'Sí' : 'No'} alerta={estado.sii_unreachable} />
      </div>
      <p className="text-xs text-content-muted">
        Última revisión: {hora(estado.checked_at)} · Último correo al equipo: {hora(estado.alert_sent_at)}
      </p>

      <div className="p-4 rounded-card border border-divider bg-glass backdrop-blur-glass space-y-4 shadow-card">
        <div>
          <SegmentGroup label="Aviso a los clientes" options={MODOS} value={modo} onChange={setModo} />
          <p className="text-xs text-content-muted mt-1.5">{MODOS.find(m => m.value === modo)?.hint}</p>
        </div>

        <TextField
          dense
          multiline
          rows={2}
          label="Texto del aviso"
          value={mensaje}
          onChange={e => setMensaje(e.target.value)}
          placeholder={estado.default_message}
          hint="Vacío = el texto por defecto. Para una mantención del SII conviene indicar hasta cuándo."
          inputProps={{ maxLength: 400 }}
        />

        <div>
          <p className="block text-xs font-medium text-content-muted mb-1">
            Así lo verán los usuarios {visible ? '' : '(hoy no se muestra)'}
          </p>
          <div className="flex items-center justify-center gap-2.5 px-10 py-2 text-sm rounded-lg bg-warning-solid text-amber-950">
            <Hourglass size={16} aria-hidden="true" />
            <p className="text-center leading-snug">
              <span className="font-semibold">Demora en el SII</span> — <span className="opacity-95">{textoFinal}</span>
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={guardar} disabled={!cambios || actualizar.isPending}>
            {actualizar.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Guardar
          </Button>
        </div>
      </div>

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}
